import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import {
  TOrganizationalEntity,
  TUserEntity,
} from 'evolve-ui/src/common/types/organizational-entity';
import { AnadoluContactGroupContact } from './anadolu-contact-groups.api';

export {
  CampaignCategory,
  CampaignChannel,
  CampaignContactSelectionType,
  CampaignContactState,
  CampaignContactStatePT,
  CampaignSendType,
  CampaignState,
  CampaignStatePT,
} from './campaigns.api';

type TAnadoluCampaignTemplate = {
  id?: string;
  name?: string;
  data?: {
    editable?: boolean;
    profile?: string;
    templateId?: string;
  };
};

export type TAnadoluCampaignContact = AnadoluContactGroupContact & {
  type?: string;
  state?: string;
  messages?: unknown[];
};

export type TAnadoluCampaign = {
  id?: string;
  type?: 'anadolu-campaign';
  from?: string;
  name?: string;
  channel?: import('./campaigns.api').CampaignChannel;
  category?: import('./campaigns.api').CampaignCategory;
  agency?: TOrganizationalEntity;
  createdByAgency?: TOrganizationalEntity;
  contactGroup?: { id?: string };
  directContacts?: number[];
  template?: TAnadoluCampaignTemplate;
  templateContent?: string;
  creator?: TUserEntity;
  relationshipNames?: string[];
  scheduled?: 'now' | '' | number;
  sentTimestamp?: number;
  state?: import('./campaigns.api').CampaignState;
  anonymized?: boolean;
  messages?: unknown;
  createdAt?: number;
  updatedAt?: number;
  contacts: TAnadoluCampaignContact[];
};

const baseUrl = `${process.env.GATSBY_CUSTOMERS_API_URL}/anadolu/campaigns`;

export const anadoluCampaignsApi = createApi({
  reducerPath: 'anadolu-campaigns',
  baseQuery: fetchBaseQuery({
    baseUrl,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['AnadoluCampaign'],
  endpoints: (builder) => ({
    getAnadoluCampaigns: builder.query<Response<TAnadoluCampaign>, TQueryParams>({
      providesTags: ['AnadoluCampaign'],
      query: formatQueryParams,
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TAnadoluCampaign>(
          res as JsonApiResponseDto<TAnadoluCampaign>,
          meta,
          args,
          true,
        ),
    }),

    getSingleAnadoluCampaign: builder.query<
      Response<TAnadoluCampaign>,
      TQueryParams & { campaignId: string }
    >({
      providesTags: ['AnadoluCampaign'],
      query: ({ campaignId, populate }) =>
        formatQueryParams({ populate }, campaignId),
      transformResponse: (response, meta, args) =>
        parseJsonApiResponse<TAnadoluCampaign>(
          response as JsonApiResponseDto<TAnadoluCampaign>,
          meta,
          args,
          true,
        ),
    }),

    getAnadoluCampaignContacts: builder.query<
      Response<TAnadoluCampaignContact>,
      TQueryParams & { campaignId: string }
    >({
      providesTags: ['AnadoluCampaign'],
      query: ({ campaignId, ...rest }) =>
        formatQueryParams(rest, `${campaignId}/contacts`),
      transformResponse: (response, meta, args) =>
        parseJsonApiResponse<TAnadoluCampaignContact>(
          response as JsonApiResponseDto<TAnadoluCampaignContact>,
          meta,
          args,
          true,
        ),
    }),

    postAnadoluCampaign: builder.mutation<
      Response<TAnadoluCampaign>,
      Omit<TAnadoluCampaign, 'id' | 'state' | 'contacts'>
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: (params) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign' },
          Request.POST,
          '',
          ['template', 'contactGroups'],
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    postAnadoluCampaignContacts: builder.mutation<
      Response<TAnadoluCampaign>,
      { campaignId: string; contactIds: string[] }
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ campaignId, contactIds }) => ({
        url: `${campaignId}/contacts`,
        body: {
          data: contactIds.map((id) => ({
            attributes: { contactId: id },
            type: 'contacts',
          })),
        },
        method: Request.POST,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    duplicateAnadoluCampaign: builder.mutation<
      Response<TAnadoluCampaign>,
      { campaignId: string }
    >({
      query: ({ campaignId }) =>
        formatRequest(
          { type: 'anadolu-campaign' },
          Request.POST,
          `${campaignId}/duplicate`,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    resendAnadoluCampaignContact: builder.mutation<
      Response<TAnadoluCampaign>,
      { campaignId: string; contactId: string }
    >({
      query: ({ campaignId, contactId }) =>
        formatRequest(
          { type: 'anadolu-campaign' },
          Request.POST,
          `${campaignId}/contacts/${contactId}/resend`,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    batchResendAnadoluCampaignContact: builder.mutation<
      { successes: number; failures: number },
      { campaignId: string; contactIds: string[] }
    >({
      query: ({ campaignId, contactIds }) =>
        formatRequest(
          { type: 'resend-messages-batch', ids: contactIds },
          Request.POST,
          `${campaignId}/contacts/resend`,
        ),
    }),

    patchAnadoluCampaign: builder.mutation<
      Response<TAnadoluCampaign>,
      Partial<TAnadoluCampaign>
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ id, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign' },
          Request.PATCH,
          id,
          ['template'],
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    patchAnadoluCampaignContact: builder.mutation<
      Response<TAnadoluCampaignContact>,
      Partial<Pick<TAnadoluCampaignContact, 'phone' | 'email'>> & {
        campaignId: string;
        contactId: string;
      }
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ campaignId, contactId, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign' },
          Request.PATCH,
          `${campaignId}/contacts/${contactId}`,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignContact>,
    }),

    scheduleAnadoluCampaign: builder.mutation<
      Response<unknown>,
      { campaignId: string; scheduled: 'now' | number }
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ campaignId, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign' },
          Request.PATCH,
          `${campaignId}/schedule`,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignContact>,
    }),

    cancelAnadoluCampaign: builder.mutation<
      Response<unknown>,
      { campaignId: string }
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ campaignId }) =>
        formatRequest(
          { type: 'anadolu-campaign' },
          Request.PATCH,
          `${campaignId}/cancel`,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignContact>,
    }),

    deleteAnadoluCampaign: builder.mutation<
      Response<TAnadoluCampaign>,
      { id: string }
    >({
      invalidatesTags: ['AnadoluCampaign'],
      query: ({ id, ...params }) =>
        formatRequest(
          { ...params, type: 'anadolu-campaign' },
          Request.DELETE,
          id,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluCampaign>,
    }),

    deleteAnadoluCampaignContacts: builder.mutation<
      Response<TAnadoluCampaignContact>,
      { campaignId: string; contactIds: string[] }
    >({
      query: ({ campaignId, contactIds }) => ({
        url: `${campaignId}/contacts`,
        body: {
          data: contactIds.map((id) => ({ id, type: 'anadolu-campaign' })),
        },
        method: Request.DELETE,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluCampaignContact>,
    }),
  }),
});

export const {
  useGetAnadoluCampaignsQuery,
  useGetSingleAnadoluCampaignQuery,
  useGetAnadoluCampaignContactsQuery,
  usePostAnadoluCampaignMutation,
  useDuplicateAnadoluCampaignMutation,
  usePostAnadoluCampaignContactsMutation,
  useResendAnadoluCampaignContactMutation,
  useBatchResendAnadoluCampaignContactMutation,
  usePatchAnadoluCampaignMutation,
  usePatchAnadoluCampaignContactMutation,
  useScheduleAnadoluCampaignMutation,
  useCancelAnadoluCampaignMutation,
  useDeleteAnadoluCampaignMutation,
  useDeleteAnadoluCampaignContactsMutation,
} = anadoluCampaignsApi;
